import { component, OnInit, prop } from '@gbds/core/src/ts/core'
import { getFirstAndLastFocusableElement } from '@gbds/core/src/ts/utils/firstlast-focusable-elements'

/**
 * @noInheritDoc
 */
@component('gb-disclosure')
export class Disclosure extends HTMLElement implements OnInit {
	@prop()
	dataDisclosureId: string

	@prop()
	disclosureContent: string

	@prop()
	buttonLabel: string

	@prop()
	disclosureTitle: string

	@prop()
	headline: string

	overlayEl: HTMLElement
	btn: HTMLButtonElement
	private wrapper: HTMLDivElement
	private disContainer: HTMLDivElement
	private disContent: HTMLDivElement
	private buttonContainer: HTMLDivElement
	private closeButton: HTMLButtonElement
	private sourceDisclosureButton: HTMLButtonElement
	private selfScriptEl: HTMLScriptElement
	private disclosureCloseLabel: string

	private boundHandleClick = this.handleClick.bind(this)

	constructor() {
		super()
	}

	onInit(): void {
		const disclosureButton = this.querySelector('button')
		if (!disclosureButton) {
			this.innerHTML = `<button>${this.innerHTML.trim()}</button>`
			this.btn = this.children[0] as HTMLButtonElement
		} else {
			this.btn = disclosureButton
		}
		this.selfScriptEl = document.querySelector('script[data-disclosure-close-button-label]') as HTMLScriptElement
		this.btn.setAttribute('aria-haspopup', 'dialog')
		this.btn.addEventListener('click', this.boundHandleClick)
	}

	disconnectedCallback() {
		if (this.btn) {
			this.btn.removeEventListener('click', this.boundHandleClick)
		}
	}

	handleClick(e: Event): void {
		e.preventDefault()
		e.stopPropagation()
		this.processDisclosureData()
			.then(disclosure => {
				if (disclosure !== null) {
					disclosure.disclosureTitle?.length ? this.btn.setAttribute('title', disclosure.disclosureTitle) : ''
					this.openDisclosure(disclosure.disclosureContent, disclosure.buttonLabel)
					this._addEscapeListener()
				}
			})
			.catch(e => {
				console.log(`Error handling disclosure click`, e)
			})
	}

	private _addEscapeListener() {
		document.addEventListener('keyup', e => this._escapeListenerHandler(e))
	}

	private _escapeListenerHandler(e: KeyboardEvent) {
		if (e.key === 'Escape' && this.disclosureOpen) {
			this._closeOpenDisclosure(e)
			e.preventDefault()
			e.stopImmediatePropagation()
		}
	}

	private async processDisclosureData() {
		const disclosureDataContent = document.querySelector('.gb-disclosure-data-content')
		this.disclosureCloseLabel = this.selfScriptEl
			? this.selfScriptEl.getAttribute('data-disclosure-close-button-label') || 'Close'
			: 'Close'
		const closeButtonLabel = this.disclosureCloseLabel
		try {
			if (this.disclosureContent) {
				const escaped = String(this.disclosureContent)
					.replace(/&/g, '&amp;')
					.replace(/</g, '&lt;')
					.replace(/>/g, '&gt;')
					.replace(/"/g, '&quot;')
					.replace(/'/g, '&#39;')
				return {
					disclosureContent: escaped,
					buttonLabel: closeButtonLabel,
					disclosureTitle: this.disclosureTitle || '',
					headline: this.headline || '',
				}
			}
			if (!disclosureDataContent) {
				return await this.fetchDisclosureData(closeButtonLabel)
			} else {
				const disclosureContent = disclosureDataContent.querySelector(
					`span[data-id="${this.dataDisclosureId}"]`
				)
				if (!disclosureContent) {
					return await this.fetchDisclosureData(closeButtonLabel)
				} else {
					const headlineData = disclosureContent.getAttribute('data-headline')
					const titleData = disclosureContent.getAttribute('data-title')
					const dataContent = disclosureContent.innerHTML

					return {
						disclosureContent: dataContent,
						buttonLabel: closeButtonLabel,
						disclosureTitle: titleData,
						headline: headlineData,
					}
				}
			}
		} catch (e) {
			console.log(`error attempting to fetch content for disclosure id ${this.dataDisclosureId}`, e)
			return null
		}
	}

	async fetchDisclosureData(closeButtonLabel: string) {
		const response = await fetch(this.dataDisclosureId + `.model.json`)
		if (!response.ok) {
			console.log(`Failed to fetch disclosure: ${response.status} ${response.statusText}`)
			return null
		}
		const data = await response.json()

		const title = data[':items']['disclaimerLibrary']['bbc-title']
		const content = data[':items']['disclaimerLibrary']['bbc-content']
		const headline = data[':items']['disclaimerLibrary']['bbc-headline']

		return {
			disclosureContent: content,
			buttonLabel: closeButtonLabel,
			disclosureTitle: title,
			headline: headline,
		}
	}

	private _disclosureTemplate = (content: string, buttonLabel: string) => {
		const template = document.createDocumentFragment()

		this.wrapper = document.createElement('div')
		this.wrapper.classList.add('gb-disclosure-wrapper')

		this.disContainer = document.createElement('div')
		this.disContainer.classList.add('gb-disclosure-container')
		this.disContainer.setAttribute('role', 'dialog')
		this.disContainer.setAttribute('aria-modal', 'true')

		this.disContent = document.createElement('div')
		this.disContent.classList.add('gb-disclosure-content')
		this.disContent.innerHTML = content

		this.buttonContainer = document.createElement('div')
		this.buttonContainer.classList.add('gb-disclosure-button-container')

		this.closeButton = document.createElement('button')
		this.closeButton.classList.add('disclosure-close-button')
		this.closeButton.addEventListener('click', this._closeOpenDisclosure.bind(this))

		this.closeButton.textContent = buttonLabel
		this.buttonContainer.appendChild(this.closeButton)

		this.disContainer.appendChild(this.disContent)
		this.disContainer.appendChild(this.buttonContainer)
		this.wrapper.appendChild(this.disContainer)
		template.appendChild(this.wrapper)

		return template
	}

	public _render(template?: DocumentFragment) {
		if (template) {
			if (this.getRootNode() instanceof ShadowRoot) {
				const shadowRoot = this.getRootNode() as ShadowRoot
				shadowRoot.appendChild(template)
			} else {
				document.body.appendChild(template)
			}
		} else {
			this.wrapper.remove()
		}
	}

	private openDisclosure(content, buttonLabel): boolean {
		const disclosureWrapper = document.querySelector('.gb-disclosure-wrapper')
		if (!disclosureWrapper) {
			this._render(this._disclosureTemplate(content, buttonLabel))
			this.sourceDisclosureButton = this.children[0] as HTMLButtonElement
			this._handleDisclosureFocusableElements()
			const closeBtn = this.closeButton
			closeBtn.focus()
			this.showDisclosure()
		} else {
			disclosureWrapper.remove()
			this.openDisclosure(content, buttonLabel)
		}
		return true
	}

	public _closeOpenDisclosure(e: Event): void {
		e.stopPropagation()
		this.closeOpenDisclosure()
		this.hideDisclosure()
	}

	public closeOpenDisclosure(): void {
		if (this.disclosureOpen) {
			this._render()
			this.sourceDisclosureButton.focus()
			this.sourceDisclosureButton = undefined
		}
	}

	private _handleDisclosureFocusableElements(): void {
		const { first, last } = getFirstAndLastFocusableElement(this.disContent)

		if (first) {
			first.focus()
			first.addEventListener('keydown', (e: KeyboardEvent) => {
				if (e.key === 'Tab' && e.shiftKey) {
					this.closeButton.focus()
					e.preventDefault()
				}
			})
		} else {
			this.closeButton.focus()
		}

		this.closeButton.addEventListener('keydown', (e: KeyboardEvent) => {
			if (e.key === 'Tab' && !e.shiftKey) {
				first?.focus()
				e.preventDefault()
			} else if (e.key === 'Tab' && e.shiftKey) {
				last ? last.focus() : first?.focus
				e.preventDefault()
			}
		})
	}

	public showDisclosure(): void {
		if (!this.overlayEl) {
			this.overlayEl = document.createElement('gb-disclosure-overlay')
			if (this.getRootNode() instanceof ShadowRoot) {
				const shadowRoot = this.getRootNode() as ShadowRoot
				shadowRoot.appendChild(this.overlayEl)
			} else {
				document.body.appendChild(this.overlayEl)
			}
		}

		if (window.browserDetectSingleton) {
			window.browserDetectSingleton.setScrollbarWidthOffset()
		}

		document.body.classList.add('gb-disclosure-open', 'gb-scroll-disabled')

		this.closeButton.focus()

		document.addEventListener('keydown', this.handleKeyDown.bind(this))
		document.body.addEventListener('click', this.handleBodyClick.bind(this))
	}

	public hideDisclosure(): void {
		if (this.overlayEl) {
			this.overlayEl.remove()
			this.overlayEl = undefined
		}
		document.body.classList.remove('gb-disclosure-open', 'gb-scroll-disabled')

		document.removeEventListener('keydown', this.handleKeyDown.bind(this))
		document.body.removeEventListener('click', this.handleBodyClick.bind(this))
	}

	private handleKeyDown(e: KeyboardEvent): void {
		const key = e.which || e.keyCode

		if (key === 27) {
			// Escape
			this._closeOpenDisclosure(e)
		}
	}

	private handleBodyClick(e): void {
		const target = e.target

		if (target.tagName === 'GB-DISCLOSURE-OVERLAY') {
			this._closeOpenDisclosure(e)
		}
	}

	public get disclosureOpen(): boolean {
		return this.wrapper.isConnected && this.wrapper.childElementCount > 0
	}
}
